import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { toast } from 'sonner';
import { ProductImage } from '../../../ui/ProductImage';
import { useCatalog } from '../../../contexts/CatalogContext';
import { useCartActions, useOverlayActions } from '../../../contexts/StoreContext';
import { useSignedInAccount } from '../../../contexts/AccountContext';
import { founder, salon } from '../../../data/content';
import { policyCopy } from '../../../data/policies';
import { useSite } from '../../../contexts/SiteContext';
import { euro } from '../../../core/format/currency';
import { ease } from '../../../core/format/motion';
import { plural } from '../../../core/format/text';
import { FALLBACK_DURATION } from '../../../core/rules/agenda';
import { canChangeDelivery, changeDeadline } from '../../../core/rules/subscription';
import { dayLong, dayShort, relative } from '../../../utils/account';
import { downloadCalendarFile } from '../../../utils/calendarFile';
import { btnOnDark, btnOnDarkQuiet, btnPriceAmount, btnPriceLabel, btnPriceLight } from '../../../ui/styles';
import type { JourneyFacts, NextStep } from '../nextStep';
import { useNextStep } from '../useNextStep';
import { DELIVERY_HREF, ACCOUNT_SALON } from '../useAccountNav';
import { OrderStatusCard } from './OrderStatusCard';
import { WeeklyTip } from './WeeklyTip';
type Content = {
  context: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  actions: ReactNode;
  media?: ReactNode;
};

/**
 * De volgende stap: het ene blok dat op het overzicht wint (regel in ../nextStep.ts).
 * Een pakket onderweg toont de bestaande pakketkaart; als er niets speelt, de tip van Sarah.
 */
export function NextStepPanel() {
  const {
    step,
    facts
  } = useNextStep();
  const reduce = useReducedMotion();
  if (step.kind === 'pakket') return <OrderStatusCard data-id="element-6194" data-mp="1t0419si88yre" />;
  if (step.kind === 'rust') return <WeeklyTip data-id="element-6195" data-mp="1szc5ohomr114" />;
  return <AnimatePresence mode="wait" initial={false} data-id="element-6196" data-mp="2inpznbijhus7">
      <motion.div key={step.kind} initial={reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} data-id="element-6197" data-mp="2in83sfzshikt">
        <StepPanel step={step} facts={facts} data-id="element-6198" data-mp="2rie1erowouc9" />
      </motion.div>
    </AnimatePresence>;
}
function StepPanel({
  step,
  facts
}: {
  step: Exclude<NextStep, {
    kind: 'pakket';
  } | {
    kind: 'rust';
  }>;
  facts: JourneyFacts;
}) {
  const content = useContent(step, facts);
  if (!content) return null;
  return <section aria-labelledby="next-step-title" className="bg-ink px-5 py-6 text-shell sm:px-8 sm:py-8" data-id="element-6199" data-mp="23sa8be9cgczi">
      <div className={content.media ? 'grid grid-cols-[minmax(0,1fr)_auto] items-start gap-5 sm:gap-8' : ''} data-id="element-6200" data-mp="2don20c4w2qu9">
        <div className="min-w-0" data-id="element-6201" data-mp="3k4z2wkz3imtj">
          <p className="text-[14px] text-shell/75 first-letter:uppercase" data-id="element-6202" data-mp="3k3nqxxvhy8rc">{content.context}</p>
          <h2 id="next-step-title" className="mt-1.5 font-display text-[1.9rem] font-light leading-[1.1] sm:text-[2.5rem]" data-id="element-6203" data-mp="3p2qvzibh8ezb">
            {content.title}
          </h2>
          {content.body && <div className="mt-3 max-w-lg text-[15px] leading-relaxed text-shell/85" data-id="element-6204" data-mp="18gu08f7n3v7v">{content.body}</div>}
        </div>
        {content.media}
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-6205" data-mp="13okbv3m6b37p">{content.actions}</div>
    </section>;
}
function useContent(step: Exclude<NextStep, {
  kind: 'pakket';
} | {
  kind: 'rust';
}>, facts: JourneyFacts): Content | null {
  const {
    policy
  } = useSite();
  const {
    account,
    subscription
  } = useSignedInAccount();
  const {
    productById,
    treatmentBySlug,
    routineForTreatment
  } = useCatalog();
  const {
    add
  } = useCartActions();
  const {
    openBooking,
    openQuiz
  } = useOverlayActions();
  const sub = account.subscription;
  switch (step.kind) {
    case 'afspraak':
      {
        const a = facts.nextAppointment?.appointment;
        if (!a) return null;
        const t = treatmentBySlug(a.treatmentSlug);
        const name = t?.name ?? a.treatmentSlug;
        const addToCalendar = () => {
          const ok = downloadCalendarFile({
            id: a.id,
            date: a.date,
            time: a.time,
            durationMinutes: t?.durationMinutes ?? FALLBACK_DURATION,
            title: `${name} bij ${salon.name}`,
            location: a.kind === 'video' ? 'Videocall (link volgt per mail)' : salon.address,
            description: `Verzetten kan tot ${policy.cancelHours} uur van tevoren via Mijn omgeving.`
          });
          if (!ok) toast.error('Toevoegen lukte niet', {
            description: 'Je vindt de uitnodiging ook in je bevestigingsmail.'
          });
        };
        return {
          context: <>
            {relative(a.date)}
            {a.status === 'aanvraag' ? ' · wacht op bevestiging' : ''}
          </>,
          title: <>
            <span className="capitalize" data-id="element-6206" data-mp="1ght6w2dsoc4p">{dayLong(a.date)}</span>, {a.time}
          </>,
          body: <p data-id="element-6207" data-mp="1kili31688r3m">
            {name}
            {a.session ? ` · sessie ${a.session}` : ''} · {a.kind === 'video' ? 'videocall, de link volgt per mail' : salon.address}
          </p>,
          actions: <>
            {a.status === 'bevestigd' && <button type="button" onClick={addToCalendar} className={btnOnDark} data-id="element-6208" data-mp="1avsqh9nsmpi2">
                In je agenda
              </button>}
            <Link to={ACCOUNT_SALON} className={btnOnDarkQuiet} data-id="element-6209" data-mp="15veg3kypz3gv">
              Verzetten of annuleren
            </Link>
          </>
        };
      }
    case 'levering':
      {
        if (!sub) return null;
        const locked = !canChangeDelivery(sub.nextDelivery, policy.subscription);
        const count = sub.items.reduce((n, i) => n + i.qty, 0) + sub.extras.length;
        return {
          context: `Je levering · ${relative(sub.nextDelivery)}`,
          title: <span className="capitalize" data-id="element-6210" data-mp="23k6nbm35bfs1">{dayLong(sub.nextDelivery)}</span>,
          body: <p data-id="element-6211" data-mp="1ylvd3a8xv39a">
            {plural(count, 'product', 'producten')}, zonder verzendkosten.{' '}
            {locked ? `${founder.firstName} pakt hem nu in; wijzigingen gelden voor de levering daarna.` : `Iets erbij, opschuiven of overslaan kan tot en met ${dayShort(changeDeadline(sub.nextDelivery, policy.subscription))}.`}
          </p>,
          actions: <Link to={DELIVERY_HREF} className={btnOnDark} data-id="element-6212" data-mp="19zlt355xuoxo">
            {locked ? 'Bekijk je levering' : 'Bekijk of pas aan'}
          </Link>
        };
      }
    case 'bijvullen':
      {
        const product = productById(step.productId);
        const low = facts.runningLow;
        if (!product || !low) return null;
        const active = sub?.status === 'actief';
        const going = Boolean(active && sub?.extras.includes(product.id));
        return {
          context: low.inDays <= 0 ? 'Waarschijnlijk al op' : `Over ± ${plural(low.inDays, 'dag', 'dagen')} op`,
          title: product.name,
          body: <p data-id="element-6213" data-mp="3upup2wogwv3c">{active ? 'Laat hem eenmalig meekomen met je volgende levering, zonder verzendkosten.' : 'Bestel hem nu, dan staat hij klaar voordat je zonder zit.'}</p>,
          media: <span className="block h-24 w-20 shrink-0 bg-shell sm:h-32 sm:w-28" aria-hidden="true" data-id="element-6214" data-mp="3n8ep2qhylupz">
            <ProductImage src={product.image} alt="" className="h-full w-full p-2" data-id="element-6215" data-mp="3idik6prmjcvg" />
          </span>,
          actions: active && sub ? <button type="button" aria-pressed={going} onClick={async () => {
            if ((await subscription({
              type: 'toggleExtra',
              productId: product.id
            })) && !going) toast.success('Gaat mee met je levering', {
              description: `${product.name}, op ${dayShort(sub.nextDelivery)}.`
            });
          }} className={btnOnDark} data-id="element-6216" data-mp="38igtvqlm44ra">
              {going && <CheckIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" data-id="element-6217" data-mp="3s555jexoesbx" />}
              {going ? `Gaat mee op ${dayShort(sub.nextDelivery)}` : 'Mee met je volgende levering'}
            </button> : <button type="button" onClick={() => add(product)} aria-label={`${product.name} in de winkelmand, ${euro(product.price)}`} className={`${btnPriceLight} sm:w-auto sm:min-w-[18rem]`} data-id="element-6218" data-mp="2gsm4jtxr8f2m">
              <span className={btnPriceLabel} data-id="element-6219" data-mp="omc40o4rjg61">In de winkelmand</span>
              <span className={btnPriceAmount} data-id="element-6220" data-mp="aqi3oti21z4j">{euro(product.price)}</span>
            </button>
        };
      }
    case 'kuur':
      {
        const c = facts.course;
        if (!c) return null;
        const t = treatmentBySlug(c.treatmentSlug);
        return {
          context: `Je kuur${t ? ` ${t.name}` : ''} · ${c.done} van ${c.total} gehad`,
          title: `Plan sessie ${step.session}`,
          body: <p data-id="element-6221" data-mp="tn019rjl4t8p">Het mooiste resultaat krijg je met een sessie {policyCopy.courseSpacing}. Plan hem gerust alvast in.</p>,
          media: <CourseDots done={c.done} total={c.total} data-id="element-6222" data-mp="ykrvwtbm6tgl" />,
          actions: <button type="button" onClick={() => openBooking(c.treatmentSlug)} aria-haspopup="dialog" className={btnOnDark} data-id="element-6223" data-mp="1mboyhfhisef6">
            Plan sessie {step.session}
          </button>
        };
      }
    case 'hervatten':
      {
        const until = facts.subscription?.pausedUntil;
        return {
          context: 'Je abonnement is gepauzeerd',
          title: until ? `Tot en met ${dayLong(until)}` : 'Even niets',
          body: <p data-id="element-6224" data-mp="2a70r3w8mcwru">Daarna gaat het vanzelf verder; je krijgt een week vooraf een seintje. Toch eerder nodig?</p>,
          actions: <>
            <button type="button" onClick={async () => {
              if (await subscription({
                type: 'resume'
              })) toast.success('Je abonnement loopt weer', {
                description: `${policyCopy.firstDelivery}.`
              });
            }} className={btnOnDark} data-id="element-6225" data-mp="1hilax6dr48ex">
              Nu al hervatten
            </button>
            <Link to={DELIVERY_HREF} className={btnOnDarkQuiet} data-id="element-6226" data-mp="1mgwlxkfs8oyq">
              Bekijk je levering
            </Link>
          </>
        };
      }
    case 'terugkomen':
      {
        const t = treatmentBySlug(step.treatmentSlug);
        const routine = routineForTreatment(step.treatmentSlug);
        return {
          context: `Je laatste bezoek was ${step.weeksAway} weken geleden`,
          title: t ? `Tijd voor je ${t.name}?` : 'Tijd voor een behandeling?',
          body: <p data-id="element-6227" data-mp="2hqc08ocr1044">
            Zelfde als vorige keer: de behandeling staat al klaar, jij kiest alleen de tijd.
            {routine ? ` Thuis houd je het vast met het ${routine.name}.` : ''}
          </p>,
          actions: <>
            <button type="button" onClick={() => openBooking(step.treatmentSlug)} aria-haspopup="dialog" className={btnOnDark} data-id="element-6228" data-mp="28om1exgjeywb">
              Zelfde als vorige keer
            </button>
            <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={btnOnDarkQuiet} data-id="element-6229" data-mp="1yuvevy3z0yj9">
              Iets anders kiezen
            </button>
          </>
        };
      }
    case 'nieuw':
      return {
        context: `Welkom bij ${founder.firstName}`,
        title: 'Waar begin je?',
        body: <p data-id="element-6230" data-mp="jv49lk6hbd2">
            De huidtest duurt één minuut en zegt welke routine bij je huid past. Liever eerst kennismaken? Online kijkt {founder.firstName}{' '}
            {policy.consult.minutes} minuten met je mee.
          </p>,
        actions: <>
            <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={btnOnDark} data-id="element-6231" data-mp="eaqilddg4ko1">
              Huidtest · 1 min
            </button>
            <button type="button" onClick={() => openBooking(policy.consult.slug)} aria-haspopup="dialog" className={btnOnDarkQuiet} data-id="element-6232" data-mp="3dr6nasw6kvh7">
              Plan een kennismaking
            </button>
          </>
      };
    default:
      return null;
  }
}

/** De kuur als rij streepjes op het donkere vlak: gehad = licht, nog te gaan = omlijnd. */
function CourseDots({
  done,
  total
}: {
  done: number;
  total: number;
}) {
  return <ol className="mt-1 grid w-24 grid-cols-3 gap-1.5 sm:w-32" aria-hidden="true" data-id="element-6233" data-mp="122f5ge7i0gxl">
      {Array.from({
      length: total
    }, (_, i) => <li key={i} className={`h-1.5 rounded-full ${i < done ? 'bg-shell' : 'ring-1 ring-inset ring-shell/40'}`} data-id="element-6234" data-mp="haqoz5y3kzbo" />)}
    </ol>;
}